import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../utils/translationHelpers';
import './LegalPage.css';

export default function TermsPage() {
  const navigate = useNavigate();
  const { t, t_nested, currentLanguage } = useLanguage();
  const lang = currentLanguage === 'nl' || currentLanguage === 'ar' ? currentLanguage : 'en';
  const backWord = getTranslation(t_nested, 'ui.back', 'Back');
  const backButtonText = lang === 'ar' ? `${backWord} →` : `← ${backWord}`;

  type Section = {
    title: string;
    paragraphs?: string[];
    bullets?: string[];
  };

  const CONTENT: Record<'en' | 'nl' | 'ar', {
    title: string;
    effective: string;
    jurisdiction: string;
    intro: string;
    sections: Section[];
    disclaimer: string;
  }> = {
    en: {
      title: 'Terms of Service',
      effective: 'Effective Apr 2026',
      jurisdiction: 'Dutch law',
      intro: 'These Terms of Service govern your use of TheoryCoach learning products, subscription features, and related services.',
      sections: [
        {
          title: '1. Service nature and scope',
          paragraphs: [
            'TheoryCoach is an independent learning and exam-preparation platform for Dutch driving theory.',
            'TheoryCoach is not affiliated with, endorsed by, or acting on behalf of CBR. Any exam-performance metric inside the app is educational and does not guarantee official exam results.',
          ],
        },
        {
          title: '2. Eligibility and account responsibilities',
          bullets: [
            'You are responsible for maintaining secure access to your account credentials.',
            'You must provide accurate account details where required.',
            'You are responsible for activity performed through your account unless caused by unauthorized access not attributable to you.',
          ],
        },
        {
          title: '3. Acceptable use',
          bullets: [
            'Use TheoryCoach for lawful, personal, and non-fraudulent educational purposes.',
            'Do not copy, redistribute, reverse engineer, or exploit app content beyond permitted use.',
            'Do not attempt to bypass subscription gates, payment flows, or security controls.',
          ],
        },
        {
          title: '4. Subscriptions, billing, and cancellation',
          paragraphs: [
            'Paid plans are processed through Stripe. Billing terms, renewal, and payment methods are governed by your selected checkout plan and Stripe processing rules.',
          ],
          bullets: [
            'Access to premium features is granted after successful payment verification.',
            'Cancelled or failed payments do not grant premium entitlement.',
            'Questions regarding billing, cancellation, and payment disputes should be submitted through the in-app support path in Settings.',
          ],
        },
        {
          title: '5. Intellectual property and license',
          paragraphs: [
            'All TheoryCoach content, interface design, and learning materials are protected by applicable intellectual property laws.',
            'You receive a limited, non-exclusive, revocable license to use the service for personal learning while your use complies with these terms.',
          ],
        },
        {
          title: '6. Availability and modifications',
          paragraphs: [
            'TheoryCoach may modify, improve, suspend, or discontinue parts of the service to maintain security, reliability, or legal compliance.',
            'Where practical, material changes affecting subscriptions or core functionality are communicated in-app.',
          ],
        },
        {
          title: '7. Liability limitations',
          paragraphs: [
            'TheoryCoach is provided on an as-available basis. To the extent permitted by law, TheoryCoach disclaims implied warranties and limits liability for indirect, incidental, or consequential damages.',
            'Nothing in these terms excludes liability where exclusion is not permitted under applicable law.',
          ],
        },
        {
          title: '8. Termination and suspension',
          bullets: [
            'TheoryCoach may suspend or terminate access in cases of abuse, fraud, security risk, or material breach of these terms.',
            'You may stop using the service at any time and can request account deletion through support.',
          ],
        },
        {
          title: '9. Governing law and disputes',
          paragraphs: [
            'These terms are governed by the laws of the Netherlands.',
            'Disputes should first be addressed through support. Where unresolved, disputes may be submitted to the competent court in the Netherlands, subject to mandatory consumer-law protections.',
          ],
        },
        {
          title: '10. Changes to terms',
          paragraphs: [
            'TheoryCoach may update these terms to reflect legal, technical, or product changes. Continued use after effective date constitutes acceptance of the updated terms.',
          ],
        },
      ],
      disclaimer: 'These terms are service terms and do not constitute legal advice.',
    },
    nl: {
      title: 'Algemene Voorwaarden',
      effective: 'Ingangsdatum apr 2026',
      jurisdiction: 'Nederlands recht',
      intro: 'Deze voorwaarden zijn van toepassing op het gebruik van TheoryCoach, inclusief leerfuncties, abonnementen en gerelateerde diensten.',
      sections: [
        {
          title: '1. Aard en omvang van de dienst',
          paragraphs: [
            'TheoryCoach is een onafhankelijk leerplatform voor voorbereiding op de Nederlandse rijtheorie.',
            'TheoryCoach is niet gelieerd aan, goedgekeurd door, of handelend namens CBR. Resultaten in de app zijn educatief en bieden geen garantie op het officiële examenresultaat.',
          ],
        },
        {
          title: '2. Toegang en accountverantwoordelijkheid',
          bullets: [
            'Je bent verantwoordelijk voor veilige omgang met je accountgegevens.',
            'Je verstrekt correcte accountinformatie waar nodig.',
            'Activiteit via jouw account valt onder jouw verantwoordelijkheid, tenzij sprake is van onbevoegde toegang die jou niet kan worden toegerekend.',
          ],
        },
        {
          title: '3. Toegestaan gebruik',
          bullets: [
            'Gebruik TheoryCoach uitsluitend rechtmatig en voor persoonlijke studiedoeleinden.',
            'Je mag content niet kopiëren, verspreiden, reverse-engineeren of commercieel exploiteren buiten toegestaan gebruik.',
            'Je mag geen betaal- of beveiligingscontroles omzeilen.',
          ],
        },
        {
          title: '4. Abonnementen, betaling en opzegging',
          paragraphs: [
            'Betaalde plannen worden verwerkt via Stripe. Facturatievoorwaarden, verlenging en betaalmethoden volgen uit het gekozen plan en Stripe-verwerking.',
          ],
          bullets: [
            'Premium toegang wordt pas verleend na succesvolle betaalverificatie.',
            'Geannuleerde of mislukte betalingen geven geen premium-rechten.',
            'Vragen over betaling, opzegging of geschillen kun je indienen via de support-route in Instellingen.',
          ],
        },
        {
          title: '5. Intellectueel eigendom en licentie',
          paragraphs: [
            'Alle TheoryCoach-content, interface-ontwerp en leermaterialen zijn beschermd door toepasselijke intellectuele-eigendomsrechten.',
            'Je ontvangt een beperkte, niet-exclusieve, herroepbare licentie voor persoonlijk gebruik zolang je deze voorwaarden naleeft.',
          ],
        },
        {
          title: '6. Beschikbaarheid en wijzigingen',
          paragraphs: [
            'TheoryCoach kan onderdelen van de dienst aanpassen, verbeteren, opschorten of beëindigen om veiligheid, betrouwbaarheid of juridische naleving te waarborgen.',
            'Waar redelijk mogelijk communiceren we materiële wijzigingen in de app.',
          ],
        },
        {
          title: '7. Aansprakelijkheidsbeperking',
          paragraphs: [
            'TheoryCoach wordt geleverd op basis van beschikbaarheid. Voor zover wettelijk toegestaan, sluiten wij impliciete garanties uit en beperken wij aansprakelijkheid voor indirecte of gevolgschade.',
            'Niets in deze voorwaarden sluit aansprakelijkheid uit waar uitsluiting wettelijk niet is toegestaan.',
          ],
        },
        {
          title: '8. Beëindiging en schorsing',
          bullets: [
            'TheoryCoach kan toegang schorsen of beëindigen bij misbruik, fraude, beveiligingsrisico of wezenlijke schending van deze voorwaarden.',
            'Je kunt op elk moment stoppen met gebruik en via support verwijdering van je account aanvragen.',
          ],
        },
        {
          title: '9. Toepasselijk recht en geschillen',
          paragraphs: [
            'Op deze voorwaarden is Nederlands recht van toepassing.',
            'Geschillen worden eerst via support geprobeerd op te lossen. Indien nodig kunnen geschillen worden voorgelegd aan de bevoegde rechter in Nederland, met inachtneming van dwingende consumentenbescherming.',
          ],
        },
        {
          title: '10. Wijzigingen van voorwaarden',
          paragraphs: [
            'TheoryCoach kan deze voorwaarden wijzigen bij juridische, technische of productwijzigingen. Voortgezet gebruik na ingangsdatum geldt als aanvaarding van de gewijzigde voorwaarden.',
          ],
        },
      ],
      disclaimer: 'Deze voorwaarden zijn dienstvoorwaarden en vormen geen juridisch advies.',
    },
    ar: {
      title: 'شروط الاستخدام',
      effective: 'سارية من أبريل 2026',
      jurisdiction: 'القانون الهولندي',
      intro: 'تحكم هذه الشروط استخدامك لخدمات TheoryCoach التعليمية والاشتراكات والميزات المرتبطة بها.',
      sections: [
        {
          title: '1. طبيعة الخدمة ونطاقها',
          paragraphs: [
            'TheoryCoach منصة تعليمية مستقلة للتحضير لاختبار القيادة النظري الهولندي.',
            'لا يرتبط TheoryCoach بـ CBR ولا يمثلها. نتائج التطبيق تعليمية ولا تشكل ضماناً لنتيجة الامتحان الرسمي.',
          ],
        },
        {
          title: '2. الأهلية ومسؤولية الحساب',
          bullets: [
            'أنت مسؤول عن حماية بيانات الدخول إلى حسابك.',
            'يلزم تقديم بيانات حساب دقيقة عند الطلب.',
            'أنت مسؤول عن النشاط المنفذ عبر حسابك ما لم يثبت وصول غير مصرح به لا يعود إليك.',
          ],
        },
        {
          title: '3. الاستخدام المقبول',
          bullets: [
            'استخدم TheoryCoach لأغراض تعليمية شخصية وبشكل قانوني.',
            'يُحظر نسخ المحتوى أو توزيعه أو الهندسة العكسية أو الاستغلال غير المصرح به.',
            'يُحظر تجاوز ضوابط الاشتراك أو الدفع أو الأمان.',
          ],
        },
        {
          title: '4. الاشتراكات والدفع والإلغاء',
          paragraphs: [
            'تُعالج الخطط المدفوعة عبر Stripe. شروط الفوترة والتجديد وطرق الدفع تخضع للخطة المختارة وقواعد Stripe.',
          ],
          bullets: [
            'تُمنح مزايا الاشتراك المدفوع بعد التحقق الناجح من الدفع.',
            'لا يمنح الدفع الملغى أو الفاشل صلاحيات مدفوعة.',
            'لطلبات الفوترة أو الإلغاء أو النزاعات، استخدم مسار الدعم داخل الإعدادات.',
          ],
        },
        {
          title: '5. الملكية الفكرية والترخيص',
          paragraphs: [
            'جميع محتويات TheoryCoach وتصميم الواجهة والمواد التعليمية محمية بموجب قوانين الملكية الفكرية.',
            'يُمنح المستخدم ترخيصاً محدوداً وغير حصري وقابلاً للإلغاء للاستخدام الشخصي المتوافق مع هذه الشروط.',
          ],
        },
        {
          title: '6. التوفر والتعديلات',
          paragraphs: [
            'يجوز لـ TheoryCoach تعديل أو تحسين أو تعليق أو إيقاف أجزاء من الخدمة لأسباب تتعلق بالأمان أو الاعتمادية أو الامتثال القانوني.',
            'عند الإمكان، يتم الإشعار بالتغييرات الجوهرية داخل التطبيق.',
          ],
        },
        {
          title: '7. حدود المسؤولية',
          paragraphs: [
            'تُقدَّم TheoryCoach على أساس التوفر. وبالقدر الذي يسمح به القانون، تُستبعد الضمانات الضمنية وتُحد مسؤولية TheoryCoach عن الأضرار غير المباشرة أو التبعية.',
            'لا يستبعد أي بند مسؤولية لا يجوز استبعادها بموجب القانون واجب التطبيق.',
          ],
        },
        {
          title: '8. الإيقاف والإنهاء',
          bullets: [
            'يجوز لـ TheoryCoach تعليق أو إنهاء الوصول عند الإساءة أو الاحتيال أو المخاطر الأمنية أو المخالفة الجوهرية للشروط.',
            'يمكنك التوقف عن الاستخدام في أي وقت وطلب حذف الحساب عبر الدعم.',
          ],
        },
        {
          title: '9. القانون الواجب التطبيق والنزاعات',
          paragraphs: [
            'تخضع هذه الشروط لقوانين هولندا.',
            'يجب محاولة حل النزاعات أولاً عبر الدعم، وعند عدم الحل يمكن عرض النزاع على المحكمة المختصة في هولندا مع مراعاة حماية المستهلك الإلزامية.',
          ],
        },
        {
          title: '10. تعديل الشروط',
          paragraphs: [
            'يجوز لـ TheoryCoach تحديث هذه الشروط وفق المتطلبات القانونية أو التقنية أو تطور المنتج. الاستمرار في الاستخدام بعد تاريخ السريان يعد قبولاً للتعديلات.',
          ],
        },
      ],
      disclaimer: 'هذه الشروط نصوص تشغيل خدمة ولا تمثل استشارة قانونية.',
    },
  };
  const tx = CONTENT[lang];

  return (
    <div className="legal-page" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="legal-page-shell">
        <div className="legal-page-header">
          <div className="legal-page-header-left">
            <button
              type="button"
              className="legal-page-back"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                  return;
                }
                navigate('/dashboard');
              }}
              aria-label={t('aria.goBack')}
            >
              {backButtonText}
            </button>
            <h1 className="legal-page-title">{tx.title}</h1>
          </div>
          <div className="legal-page-meta">
            <span className="legal-chip">{tx.effective}</span>
            <span className="legal-chip">{tx.jurisdiction}</span>
          </div>
        </div>

        <div className="legal-page-content">
          <p className="legal-intro">{tx.intro}</p>

          {tx.sections.map((section) => (
            <section className="legal-section" key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs?.map((p, idx) => (
                <p key={`${section.title}-p-${idx}`}>{p}</p>
              ))}
              {section.bullets && (
                <ul>
                  {section.bullets.map((item, idx) => (
                    <li key={`${section.title}-li-${idx}`}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <p className="legal-disclaimer">{tx.disclaimer}</p>
          <p className="legal-updated">{tx.effective}</p>
        </div>
      </div>
    </div>
  );
}
